import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseRounded from "@mui/icons-material/CloseRounded";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { PanelIconButton } from "../sidebar/shared/panelChrome";
import { FormFooter } from "../forms/FormPrimitives";
import { FORM, formSx, px } from "../../theme/formStyles";

/**
 * The app's confirmation modal — one shell for every "are you sure?" across the
 * loan drilldown and the sidebars.
 *
 * Built on the drilldown form system (FORM_STYLE_GUIDE.md / theme/formStyles),
 * so it reads as the same object as the drilldown's add/edit dialogs:
 *
 *   header  `formSx.confirmHead` — 18/600 title, optional 12.5 helper line,
 *                               and the panel chrome's close button
 *                               (`PanelIconButton`, 30px square) so the X is
 *                               the same control the resizeable sidebars use.
 *                               No hairline: a confirmation is one question,
 *                               not a form with regions to separate.
 *   body    `formSx.confirmBody`
 *   footer  `FormFooter`      — hairline on top (that rule DOES earn its
 *                               place — it separates the question from the
 *                               actions), optional status text on the left,
 *                               actions right: neutral outlined Cancel + the
 *                               theme's primary Confirm.
 *
 * Button colors come from the theme variants, never from a typed hex (see the
 * button rule in CLAUDE.md): Cancel is `outlined`/`gray`, Confirm is the
 * default primary, or `error` when `destructive` is set.
 *
 * Props:
 * - open: boolean — whether the modal is visible
 * - title: node — the dialog's title
 * - description: node (optional) — one quiet line under the title, in the
 *     header. For the sentence the user has to read before deciding use
 *     `message` (it belongs in the body), not this.
 * - message: node — the main content
 * - children: node (optional) — extra body content under `message`
 * - status: node (optional) — quiet text in the footer's left slot
 * - confirmText / cancelText: string — button labels
 * - destructive: boolean — paints the confirm button as the error action
 * - confirmDisabled: boolean
 * - hideClose: boolean — drop the header X (the modal is then Cancel-only)
 * - maxWidth: MUI Dialog maxWidth — "sm" (600px) is the house width for a
 *     confirmation; pass "md" only when the body is genuinely a form.
 * - loading: boolean — spinner on the confirm button, both buttons disabled
 * - onConfirm / onCancel: () => void
 *
 * Example:
 *   <ConfirmationDialog
 *     open={confirmDelete}
 *     title="Delete borrower?"
 *     message="This also removes their association with any files."
 *     confirmText="Delete"
 *     destructive
 *     loading={deleting}
 *     onConfirm={handleConfirmDelete}
 *     onCancel={() => setConfirmDelete(false)}
 *   />
 */
const ConfirmationDialog = ({
  open,
  // Optional sx for the Dialog root — e.g. a zIndex when it opens over a
  // surface that sits above the modal layer (the drilldown sidebars).
  sx,
  title,
  description,
  message,
  children,
  status,
  confirmText = "Confirm",
  cancelText = "Cancel",
  destructive = false,
  confirmDisabled = false,
  hideClose = false,
  onConfirm,
  maxWidth = "sm",
  onCancel,
  loading,
}) => {
  return (
    <Dialog
      open={open}
      sx={sx}
      maxWidth={maxWidth}
      fullWidth
      onClose={loading ? undefined : onCancel}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      PaperProps={{ sx: { borderRadius: px(FORM.radius.modal) } }}
    >
      {/* header — title + optional helper, close X; no hairline */}
      {title && (
        <Box sx={formSx.confirmHead}>
          <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
            <Box sx={{ minWidth: 0 }} id="confirmation-dialog-title">
              <Txt sx={formSx.confirmTitle}>{title}</Txt>
              {description && (
                <Txt sx={formSx.titleHelper}>{description}</Txt>
              )}
            </Box>
            {!hideClose && (
              // Nudged into the header's padding so the 30px hit target lines
              // up with the title's cap height and the panel's right edge.
              <PanelIconButton
                icon={CloseRounded}
                title="Close"
                onClick={onCancel}
                disabled={loading}
                sx={{ mt: px(-4), mr: px(-6), flexShrink: 0 }}
              />
            )}
          </Flx>
        </Box>
      )}

      {(message || children) && (
        <DialogContent
          sx={{
            ...formSx.confirmBody,
            // confirmBody leans on the header's 12px bottom for its top gap;
            // with no header there is nothing to lean on.
            ...(title ? null : { pt: px(FORM.s.s6) }),
          }}
        >
          {message && (
            <Txt
              component="div"
              id="confirmation-dialog-description"
              sx={formSx.body}
            >
              {message}
            </Txt>
          )}
          {children}
        </DialogContent>
      )}

      {/* footer — hairline on top, actions right */}
      <FormFooter status={status}>
        <Button
          variant="outlined"
          color="gray"
          onClick={onCancel}
          disabled={loading}
          sx={formSx.button}
        >
          {cancelText}
        </Button>
        <Button
          loading={loading}
          disabled={confirmDisabled}
          onClick={() => onConfirm?.()}
          {...(destructive ? { color: "error" } : {})}
          variant="contained"
          autoFocus
          sx={formSx.button}
        >
          {confirmText}
        </Button>
      </FormFooter>
    </Dialog>
  );
};

export default ConfirmationDialog;
